/**
 * Parses an "HH:MM" or "HH:MM:SS" string into its total minutes since
 * midnight, or returns `null` for any malformed / out-of-range input.
 *
 * Used by modules (`crossMidnight`, `nightHours`, ...) whose business rule
 * is "invalid input ⇒ 0 minutes" rather than "invalid input ⇒ throw".
 * Modules that must throw on bad input (e.g. `roundUpQuarter`) intentionally
 * keep their own strict parser and are not expected to share this helper.
 *
 * - Seconds are accepted in the input for format validation but ignored in
 *   the returned minute count (quarter-hour billing never needs sub-minute
 *   precision).
 * - Hours are bounded to 0..23, minutes and seconds to 0..59.
 *
 * Why: centralising this parser guarantees both duration and night-minutes
 * computations agree on exactly the same set of "valid" inputs — removing
 * a silent source of drift between two otherwise independent rules.
 */

const MINUTES_PER_HOUR = 60

const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/

export function parseHhmmToMinutes(time: string): number | null {
  const match = TIME_PATTERN.exec(time)
  if (match === null) {
    return null
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  const seconds = match[3] === undefined ? 0 : Number(match[3])

  if (hours > 23 || minutes > 59 || seconds > 59) {
    return null
  }

  return hours * MINUTES_PER_HOUR + minutes
}
